import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, PhoneCall, Bot, User, ShieldAlert, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

type Outcome =
  | "queued" | "dialing" | "no_answer" | "declined" | "email_captured"
  | "voicemail" | "wrong_number" | "do_not_call" | "guardrail_aborted"
  | "cost_cap_aborted" | "carrier_failed" | "system_error";

interface TranscriptTurn {
  speaker: "assistant" | "contact" | "system";
  text: string;
  ts: string;
  guardTripped?: boolean;
}

interface AttemptRow {
  id: string;
  outcome: Outcome;
  capturedEmail: string | null;
  dialedNumberE164: string;
  costCents: number;
  durationSeconds: number;
  errorReason: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  transcript: TranscriptTurn[];
}

const OUTCOME_LABELS: Record<Outcome, string> = {
  queued: "Queued",
  dialing: "Dialing",
  no_answer: "No answer",
  declined: "Contact declined",
  email_captured: "Email captured",
  voicemail: "Voicemail",
  wrong_number: "Wrong number",
  do_not_call: "Do-not-call",
  guardrail_aborted: "Safety guard aborted",
  cost_cap_aborted: "Cost cap reached",
  carrier_failed: "Carrier failed",
  system_error: "System error",
};

function outcomeVariant(o: Outcome): "default" | "secondary" | "destructive" | "outline" {
  if (o === "email_captured") return "default";
  if (o === "guardrail_aborted" || o === "system_error" || o === "carrier_failed" || o === "cost_cap_aborted") return "destructive";
  if (o === "do_not_call" || o === "wrong_number" || o === "declined") return "secondary";
  return "outline";
}

function formatDuration(s: number): string {
  if (!s || s < 0) return "0s";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

function formatCost(c: number): string {
  return `$${(c / 100).toFixed(2)}`;
}

interface Props {
  leadId: string;
  leadName?: string | null;
  contactName?: string | null;
}

export function LeadOutboundCallCard({ leadId, leadName, contactName }: Props) {
  const { data: attempts = [], isLoading } = useQuery<AttemptRow[]>({
    queryKey: ["/api/leads", leadId, "outbound-call-attempts"],
    staleTime: 0,
    refetchOnMount: "always",
  });

  if (isLoading || attempts.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="card-lead-outbound-calls">
      <h4 className="font-medium flex items-center gap-2">
        <PhoneCall className="w-4 h-4 text-blue-500" />
        AI Outbound Calls
        <span className="text-xs font-normal text-muted-foreground">({attempts.length})</span>
      </h4>
      <div className="space-y-2">
        {attempts.map((a) => (
          <AttemptCard key={a.id} attempt={a} leadName={leadName} contactName={contactName} />
        ))}
      </div>
    </div>
  );
}

function AttemptCard({
  attempt,
  leadName,
  contactName,
}: {
  attempt: AttemptRow;
  leadName?: string | null;
  contactName?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const summary =
    attempt.outcome === "email_captured" && attempt.capturedEmail
      ? `Captured email`
      : OUTCOME_LABELS[attempt.outcome];

  const composeSubject = encodeURIComponent(
    `Following up on our call${leadName ? ` — ${leadName}` : ""}`,
  );
  const composeBody = encodeURIComponent(
    `Hi${contactName ? ` ${contactName.split(" ")[0]}` : ""},\n\nThanks for chatting with our assistant. Following up here to continue the conversation.\n\n`,
  );

  return (
    <div
      className="border rounded-lg p-3 bg-card"
      data-testid={`card-outbound-attempt-${attempt.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <PhoneCall className="w-4 h-4 mt-0.5 text-blue-500 shrink-0" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium" data-testid={`text-outbound-outcome-${attempt.id}`}>
                {summary}
              </span>
              <Badge variant={outcomeVariant(attempt.outcome)} className="text-xs">
                {OUTCOME_LABELS[attempt.outcome]}
              </Badge>
            </div>
            {attempt.outcome === "email_captured" && attempt.capturedEmail && (
              <div className="mt-1 flex items-center gap-1 text-sm">
                <Mail className="w-3.5 h-3.5 text-green-600" />
                <a
                  href={`mailto:${attempt.capturedEmail}?subject=${composeSubject}&body=${composeBody}`}
                  className="text-primary underline-offset-2 hover:underline break-all"
                  data-testid={`link-compose-followup-${attempt.id}`}
                >
                  {attempt.capturedEmail}
                </a>
              </div>
            )}
            {attempt.errorReason && (
              <p className="text-xs text-destructive mt-1" data-testid={`text-outbound-error-${attempt.id}`}>
                {attempt.errorReason}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {new Date(attempt.createdAt).toLocaleString()} · {attempt.dialedNumberE164} ·{" "}
              {formatDuration(attempt.durationSeconds)} · {formatCost(attempt.costCents)}
            </p>
          </div>
        </div>
        {attempt.transcript.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            data-testid={`button-toggle-transcript-${attempt.id}`}
            className="shrink-0"
          >
            {open ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
            Transcript
          </Button>
        )}
      </div>
      {open && attempt.transcript.length > 0 && (
        <div
          className="mt-3 border-t pt-3 space-y-2"
          data-testid={`region-transcript-${attempt.id}`}
        >
          {attempt.transcript.map((turn, i) => (
            <TranscriptRow key={i} turn={turn} attemptId={attempt.id} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function TranscriptRow({
  turn,
  attemptId,
  index,
}: {
  turn: TranscriptTurn;
  attemptId: string;
  index: number;
}) {
  const isGuard = turn.guardTripped === true;
  const isSystem = turn.speaker === "system";
  const isAssistant = turn.speaker === "assistant";

  if (isGuard) {
    return (
      <div
        className="flex gap-2 text-xs p-2 rounded border border-destructive/40 bg-destructive/5"
        data-testid={`row-transcript-${attemptId}-${index}`}
      >
        <ShieldAlert className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="font-medium text-destructive">Hard-stop guard</div>
          <div className="text-muted-foreground break-words">{turn.text}</div>
          {turn.ts && (
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {new Date(turn.ts).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isSystem) {
    return (
      <div
        className="text-xs italic text-muted-foreground px-2"
        data-testid={`row-transcript-${attemptId}-${index}`}
      >
        — {turn.text}
      </div>
    );
  }

  return (
    <div
      className={`flex gap-2 ${isAssistant ? "" : "flex-row-reverse"}`}
      data-testid={`row-transcript-${attemptId}-${index}`}
    >
      <div className="shrink-0 mt-1">
        {isAssistant ? (
          <Bot className="w-4 h-4 text-blue-500" />
        ) : (
          <User className="w-4 h-4 text-emerald-600" />
        )}
      </div>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
          isAssistant
            ? "bg-blue-500/10 text-foreground"
            : "bg-emerald-500/10 text-foreground"
        }`}
      >
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
          {isAssistant ? "Assistant" : "Contact"}
        </div>
        <div className="break-words whitespace-pre-wrap">{turn.text}</div>
        {turn.ts && (
          <div className="text-[10px] text-muted-foreground mt-1">
            {new Date(turn.ts).toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
}
